import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import apiClient from "../apiClient";
import { formatMoney } from "../formatMoney";
import PageContext from "../PageContext.jsx";

const TransactionsUpdate = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const transactionId = location.pathname.split("/")[2];

  const [transactionTypes, setTransactionTypes] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [defaultCurrency, setDefaultCurrency] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [deleting, setDeleting] = useState(false);

  const [transaction, setTransaction] = useState({
    Description: "",
    Amount: "",
    TransactionTypeID: "",
    SubcategoryId: "",
    Date: "",
    PeriodId: "",
  });

  const sortedTransactionTypes = useMemo(
    () => [...transactionTypes].sort((a, b) => (a.Description || "").localeCompare(b.Description || "")),
    [transactionTypes]
  );
  const sortedSubcategories = useMemo(
    () => [...subcategories].sort((a, b) => (a.SubcategoryDescription || "").localeCompare(b.SubcategoryDescription || "")),
    [subcategories]
  );
  const sortedPeriods = useMemo(
    () => [...periods].sort((a, b) => (a.Description || "").localeCompare(b.Description || "")),
    [periods]
  );

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [transactionRes, typesRes, subcategoriesRes, periodsRes, currenciesRes] = await Promise.all([
          apiClient.get(`/transactions/${transactionId}`),
          apiClient.get("/transactiontypes"),
          apiClient.get("/subcategories"),
          apiClient.get("/periods"),
          apiClient.get("/currencies"),
        ]);

        const current = transactionRes.data || {};
        setTransaction({
          Description: current.Description || "",
          Amount: current.Amount ?? "",
          TransactionTypeID: current.TransactionTypeID ? String(current.TransactionTypeID) : "",
          SubcategoryId: current.SubcategoryId ? String(current.SubcategoryId) : "",
          Date: current.Date || "",
          PeriodId: current.PeriodId ? String(current.PeriodId) : "",
        });

        setTransactionTypes(Array.isArray(typesRes.data) ? typesRes.data : []);
        setSubcategories(Array.isArray(subcategoriesRes.data) ? subcategoriesRes.data : []);
        setPeriods(Array.isArray(periodsRes.data) ? periodsRes.data : []);
        const currencyRows = Array.isArray(currenciesRes.data) ? currenciesRes.data : [];
        setDefaultCurrency(currencyRows.find((row) => Number(row.IsDefault))?.Description || "");
      } catch (err) {
        setError("Failed to load transaction details.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [transactionId]);

  const handleChange = (e) => {
    setTransaction((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSave = async (e) => {
    e.preventDefault();

    if (
      !transaction.Description?.trim() ||
      transaction.Amount === "" ||
      Number.isNaN(Number(transaction.Amount)) ||
      !transaction.TransactionTypeID ||
      !transaction.SubcategoryId ||
      !transaction.Date ||
      !transaction.PeriodId
    ) {
      setError("Please fill in all required fields.");
      return;
    }

    try {
      await apiClient.put(`/transactions/${transactionId}`, {
        ...transaction,
        Amount: Number(transaction.Amount),
      });
      navigate(`/Transactions?periodId=${transaction.PeriodId}`);
    } catch (err) {
      setError("Could not update transaction. Please try again.");
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Delete this transaction?")) return;

    setDeleting(true);
    setError("");
    try {
      await apiClient.delete(`/transactions/${transactionId}`);
      navigate(`/Transactions?periodId=${transaction.PeriodId}`);
    } catch (err) {
      setError("Could not delete transaction. Please try again.");
      setDeleting(false);
    }
  };

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1 className="page-title">Update Transaction</h1>
          <p className="page-subtitle">
            <Link to="/">Home</Link> / <Link to="/Transactions">Transactions</Link> / Update
          </p>
        </div>
      </header>
      <PageContext
        items={[
          { label: "Home", to: "/" },
          { label: "Transactions", to: "/Transactions" },
          { label: "Update Transaction" },
        ]}
        note={transaction.Description ? `Editing ${transaction.Description}.` : "Update the selected transaction details."}
      />

      <section className="panel form-card">
        {loading ? (
          <p className="notice notice-muted">Loading transaction...</p>
        ) : (
          <div className="form">
            <label htmlFor="transaction-description">Description</label>
            <input
              id="transaction-description"
              type="text"
              name="Description"
              value={transaction.Description}
              onChange={handleChange}
            />

            <label htmlFor="transaction-type">Transaction Type</label>
            <select
              id="transaction-type"
              name="TransactionTypeID"
              value={transaction.TransactionTypeID}
              onChange={handleChange}
            >
              {sortedTransactionTypes.map((item) => (
                <option key={item.TransactionTypeID} value={item.TransactionTypeID}>
                  {item.Description}
                </option>
              ))}
            </select>

            <label htmlFor="transaction-subcategory">Subcategory</label>
            <select
              id="transaction-subcategory"
              name="SubcategoryId"
              value={transaction.SubcategoryId}
              onChange={handleChange}
            >
              {sortedSubcategories.map((item) => (
                <option key={item.SubcategoryId} value={item.SubcategoryId}>
                  {item.SubcategoryDescription} ({item.CategoryDescription})
                </option>
              ))}
            </select>

            <label htmlFor="transaction-amount">Amount</label>
            <input
              id="transaction-amount"
              type="number"
              step="0.01"
              name="Amount"
              value={transaction.Amount}
              onChange={handleChange}
            />
            <div className="form-value-preview">
              Display: {formatMoney(transaction.Amount || 0, defaultCurrency)}
            </div>

            <label htmlFor="transaction-date">Date</label>
            <input
              id="transaction-date"
              type="date"
              name="Date"
              value={transaction.Date}
              onChange={handleChange}
            />

            <label htmlFor="transaction-period">Period</label>
            <select id="transaction-period" name="PeriodId" value={transaction.PeriodId} onChange={handleChange}>
              {sortedPeriods.map((item) => (
                <option key={item.PeriodId} value={item.PeriodId}>
                  {item.Description}
                </option>
              ))}
            </select>

            <div className="form-actions">
              <button className="btn" onClick={handleSave}>
                Save Changes
              </button>
              <button className="btn-link" type="button" onClick={handleDelete} disabled={deleting}>
                {deleting ? "Deleting..." : "Delete"}
              </button>
              <Link className="btn-link" to="/Transactions">
                Cancel
              </Link>
            </div>

            {error && <p className="notice notice-error">{error}</p>}
          </div>
        )}
      </section>
    </div>
  );
};

export default TransactionsUpdate;
